import {
  type CustomerUserConfigNavigationItem,
  DEFAULT_MAX_ROWS,
  IconName,
  type RequiredProperties,
  WL_ACTION,
  type WLOrgConfig,
} from '@talos/kyoko';
import { noop } from 'lodash-es';
import { Addresses } from '../../components/Addresses';
import { Balances } from '../../components/Balances';
import { CreditExposuresBlotter } from '../../components/Blotters/CreditExposuresBlotter';
import { MobileBlotter } from '../../components/Blotters/MobileBlotter';
import { OrderHistoryBlotter } from '../../components/Blotters/OrderHistoryBlotter';
import { TradesBlotter } from '../../components/Blotters/TradesBlotter';
import { TransfersBlotter } from '../../components/Blotters/TransfersBlotter';
import { CandleSticksWithSymbol } from '../../components/Candlesticks/CandleSticksWithSymbol';
import { FeeTiers } from '../../components/FeeTiers';
import { OrderCards } from '../../components/OrderCards';
import { OrderForms } from '../../components/OrderForms';
import { RFQOrderForm } from '../../components/OrderForms/RFQForm';
import { Settings } from '../../components/Settings';
import { VolumeLadder } from '../../components/VolumeLadder';
import { MobileWatchList } from '../../components/WatchList/MobileWatchList';

interface ScreenNavigationItem extends RequiredProperties<CustomerUserConfigNavigationItem, 'icon'> {
  component: JSX.Element;
  requiredConfigs?: (keyof WLOrgConfig)[];
  id: string;
}

export const WatchListScreen: ScreenNavigationItem = {
  id: 'watchlist',
  component: <MobileWatchList securityRoute="/ladder" />,
  label: 'watchlist' as const,
  path: '/',
  icon: IconName.ViewList,
};

export const OrderFormsScreen: ScreenNavigationItem = {
  id: 'order-forms',
  component: <OrderForms />,
  label: 'trade' as const,
  path: '/trade',
  icon: IconName.TrendUp,

  requiredPermissions: [WL_ACTION.INITIATE_ORDER],
};

export const RFQFormScreen: ScreenNavigationItem = {
  id: 'rfq-form',
  component: <RFQOrderForm />,
  label: 'trade' as const,
  path: '/trade',
  icon: IconName.TrendUp,

  requiredPermissions: [WL_ACTION.INITIATE_RFQ],
};

export const OrderCardsScreen: ScreenNavigationItem = {
  id: 'order-cards',
  component: <OrderCards />,
  label: 'orders' as const,
  path: '/orders',
  icon: IconName.Collection,
  requiredPermissions: [WL_ACTION.VIEW_BLOTTERS_ACTIVE_ORDERS],
};

export const BalancesScreen: ScreenNavigationItem = {
  id: 'balances',
  component: <Balances />,
  label: 'balances' as const,
  path: '/balances',
  icon: IconName.ChartDonut,
  requiredPermissions: [WL_ACTION.VIEW_BLOTTERS_BALANCES],
};

export const TradesScreen: ScreenNavigationItem = {
  id: 'trades',
  component: (
    <MobileBlotter titleKey="trades">
      <TradesBlotter
        renderInBackground={true}
        loading={false}
        maxRows={DEFAULT_MAX_ROWS}
        onStartLoading={noop}
        onFinishedLoading={noop}
        suppressShowOnlySelfOrdersAndTrades={true}
      />
    </MobileBlotter>
  ),
  label: 'trades' as const,
  path: '/trades',
  icon: IconName.DocumentText,
  requiredPermissions: [WL_ACTION.VIEW_BLOTTERS_TRADES],
};

export const CreditScreen: ScreenNavigationItem = {
  id: 'credit',
  component: (
    <MobileBlotter titleKey="credit">
      <CreditExposuresBlotter renderInBackground={true} />
    </MobileBlotter>
  ),
  label: 'credit' as const,
  path: '/credit',
  icon: IconName.CurrencyDollar,
  requiredConfigs: ['showExposureBlotter'],
  requiredPermissions: [WL_ACTION.VIEW_BLOTTERS_CREDITS],
};

export const TransfersScreen: ScreenNavigationItem = {
  id: 'transfers',
  component: (
    <MobileBlotter titleKey="transfers">
      <TransfersBlotter renderInBackground={true} maxRows={DEFAULT_MAX_ROWS} />
    </MobileBlotter>
  ),
  label: 'transfers' as const,
  path: '/transfers',
  icon: IconName.ArrowLeftRight,
  requiredPermissions: [WL_ACTION.VIEW_BLOTTERS_TRANSFERS],
  requiredConfigs: ['enableBalanceTransactions'] satisfies (keyof WLOrgConfig)[],
};

export const OrdersScreen: ScreenNavigationItem = {
  id: 'orders',
  component: (
    <MobileBlotter titleKey="orders">
      <OrderHistoryBlotter
        renderInBackground={true}
        loading={false}
        maxRows={DEFAULT_MAX_ROWS}
        onStartLoading={noop}
        onFinishedLoading={noop}
        suppressShowOnlySelfOrdersAndTrades={true}
      />
    </MobileBlotter>
  ),
  label: 'orders' as const,
  path: '/orders',
  icon: IconName.DocumentText,
  requiredPermissions: [WL_ACTION.VIEW_BLOTTERS_ORDER_HISTORY],
};

export const SettingsScreen: ScreenNavigationItem = {
  id: 'settings',
  component: <Settings />,
  label: 'settings' as const,
  path: '/settings',
  icon: IconName.Cog,
  requiredPermissions: [WL_ACTION.VIEW_SETTINGS],
};

export const LadderScreen: ScreenNavigationItem = {
  id: 'ladder',
  component: <VolumeLadder />,
  label: 'ladder' as const,
  path: '/ladder',
  icon: IconName.Filter,
};
export const ChartScreen: ScreenNavigationItem = {
  id: 'chart',
  component: <CandleSticksWithSymbol />,
  label: 'chart' as const,
  path: '/chart',
  icon: IconName.PresentationChartLine,
  requiredPermissions: [WL_ACTION.SHOW_CANDLESTICKS_AND_MARKETDATA],
};

export const AddressesScreen: ScreenNavigationItem = {
  id: 'addresses',
  component: <Addresses />,
  label: 'addresses' as const,
  path: '/addresses',
  icon: IconName.Link,
};

export const FeeTiersScreen: ScreenNavigationItem = {
  id: 'fee-tiers',
  component: <FeeTiers />,
  label: 'feeTiers' as const,
  path: '/fee-tiers',
  icon: IconName.Fees,
  requiredPermissions: [WL_ACTION.VIEW_FEE_TIERS],
};
